import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { Rocket, ShieldAlert, ClipboardCheck, Clock, Activity, TrendingUp, Compass, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { StackSection, Reveal, SectionHeading } from '../../components/scroll';
import { AdoptionTimeline, TiltCard, ScrambleText, LiveWorkConsole, WorkPipelineFlow, StatCard } from '../../components/infographics';
import { StarsBackground } from '../../components/StarsBackground';
import { imgDataNetwork, imgMatrixRain, imgDataCity } from '../../components/sticky-backgrounds';

const progress = [
  {
    icon: Activity,
    title: 'Current status',
    body: 'TICCA is in active development. We are building a secure, robust, redundant, and scalable foundation for both the product and the company itself.',
    tag: 'Active development',
  },
  {
    icon: TrendingUp,
    title: 'Our progress',
    body: 'Our product development is always ongoing as we refine, enhance, and add features based on our vision, user feedback, and market research. Transparency and trust are integral to everything we do.',
    tag: 'Secure and transparent',
  },
  {
    icon: Compass,
    title: 'Looking ahead',
    body: 'We remain excited about the advances we make every day. Our commitment is to continuously deliver a reliable platform that makes AI-assisted work more efficient and accountable for all.',
    tag: 'Daily innovation',
  },
];

const timing = [
  { icon: Rocket, title: 'AI adoption is entering operations', body: 'AI is moving from chat experiments into real, everyday work.' },
  { icon: ShieldAlert, title: 'The governance gap is becoming urgent', body: 'Control, audit, and accountability must catch up with capability.' },
  { icon: ClipboardCheck, title: 'Proof is becoming a board-level concern', body: 'Cost visibility and evidence are rising to executive priority.' },
  { icon: Clock, title: 'TICCA has been building ahead of demand', body: 'We have been building before the market fully asks for it.' },
];

export function WhenPage({ onNavigateToInvestor }: { onNavigateToInvestor?: () => void }) {
  const navigate = useNavigate();
  const handleInvestor = onNavigateToInvestor || (() => navigate('/investor'));
   useEffect(() => {
    
    window.scrollTo(0, 0);
    document.title = "TICCA | WHEN we launch";
  }, []);

  return (
    <div className="relative bg-slate-950">
      <section
        className="relative overflow-hidden flex min-h-[70vh] items-center px-6 pt-28 pb-16"
      >
        <div className="absolute bottom-0 inset-x-0 h-32 bg-gradient-to-t from-slate-950/60 to-transparent pointer-events-none" />
        <StarsBackground />
        <div className="relative z-10 mx-auto w-full max-w-4xl">
          <div className="text-left">
            <Reveal>
              <span className="mb-4 inline-block rounded-full border border-[#074EDC]/60 bg-[#074EDC]/15 px-4 py-1 uppercase tracking-[0.20em] text-[#74aaff] backdrop-blur-sm text-xs font-semibold">
                When: Timing is Everything
              </span>
            </Reveal>
            <Reveal delay={0.06}>
              <h1 className="text-balance text-white text-4xl md:text-5xl font-extrabold leading-[1.08]">Timing is everything.</h1>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="mx-auto mt-6 max-w-2xl text-white/95 lg:mx-0">
                The enterprise AI operating layer is an early and important category. It is forming
                now. As AI moves from experiments into real work, organisations will need systems that
                make AI activity governable, auditable, safer, and commercially useful. We believe
                that by 2028, every enterprise will need an AI operating layer. The question is whose.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── THE GOVERNANCE GAP, MEASURED ──────────────────────── */}
      <StackSection theme="navy">
        <SectionHeading
          eyebrow="Market Research"
          theme="navy"
          title="A category forming in real time."
          intro="Independent research firms are tracking the gap TICCA is building for."
        />
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          <Reveal delay={0}>
            <StatCard
              number="75%"
              label="of organisations have already integrated AI into their operations"
              source="Gartner, AI Ethics Governance and Compliance 2026"
              accent="#ED0216"
            />
          </Reveal>
          <Reveal delay={0.06}>
            <StatCard
              number="<25%"
              label="of IT leaders are confident they can manage AI governance"
              source="Gartner, AI Ethics Governance and Compliance 2026"
              accent="#FF8C00"
            />
          </Reveal>
          <Reveal delay={0.12}>
            <StatCard
              number="75%"
              label="of the world's economies will be covered by fragmented AI regulation by 2030"
              source="Gartner Forecast"
              accent="#074EDC"
            />
          </Reveal>
        </div>
        <Reveal delay={0.18}>
          <div className="mx-auto mt-10 max-w-3xl text-center">
            <blockquote
              className="rounded-2xl border border-white/15 border-l-4 bg-[rgba(2,8,28,0.88)] backdrop-blur-2xl px-8 py-6"
              style={{ borderLeftColor: '#4AAC00' }}
            >
              <p className="text-white/80 text-lg leading-relaxed italic">
                "a widening gap between what AI can do and how prepared we are to manage it"
              </p>
              <footer className="mt-4 uppercase tracking-[0.12em] text-xs" style={{ color: '#4AAC00' }}>
                Stanford HAI AI Index Report 2026
              </footer>
            </blockquote>
            <p className="mt-6 text-white/60 text-sm leading-relaxed">
              The operating infrastructure to support governed AI work does not yet meaningfully
              exist. The question is not whether it will be built, but who builds it first.
            </p>
          </div>
        </Reveal>
      </StackSection>

      {/* Data network nodes, distributed AI entering operations NOW */}
      <section className="relative py-20 px-6 sm:px-10">
        <Reveal>
          <div className="mb-10 text-center">
            <span className="mb-3 inline-block uppercase tracking-[0.14em] text-xs text-[#3b82f6]">Why now</span>
            <h2 style={{ color: '#4AAC00' }}>Four signals that say the moment is now.</h2>
          </div>
        </Reveal>
        <div className="grid gap-6 md:grid-cols-2">
          {timing.map(({ icon: Icon, title, body }, i) => (
            <Reveal key={title} delay={i * 0.08}>
              <TiltCard tilt={4}>
                <div className="flex h-full items-start gap-5 rounded-2xl border border-white/15 border-l-4 border-l-[#074EDC] bg-[#020b1a]/90 backdrop-blur-xl p-7 shadow-lg hover:bg-[#020b1a] hover:border-[#074EDC]/55 hover:shadow-xl transition-all duration-200">
                  <motion.div
                    whileHover={{ rotate: -8, scale: 1.08 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 15 }}
                    className="mt-0.5 shrink-0 rounded-xl bg-[#074EDC] p-3"
                  >
                    <Icon className="h-6 w-6 text-white" />
                  </motion.div>
                  <div>
                    <h3 className="mb-1.5 text-white">{title}</h3>
                    <p className="text-white/85">{body}</p>
                  </div>
                </div>
              </TiltCard>
            </Reveal>
          ))}
        </div>
      </section>


      {/* Matrix code rain, data flowing, AI in operations */}
      <section className="relative py-20 px-6 sm:px-10">
        <Reveal>
          <div className="mb-10 text-center">
            <span className="mb-3 inline-block uppercase tracking-[0.14em] text-xs" style={{ color: '#4AAC00' }}>Momentum</span>
            <h2 style={{ color: '#4AAC00' }}>Where we are right now.</h2>
            <p className="mx-auto mt-4 max-w-xl text-white/85">
              We would rather show honest progress than overstate our stage.
            </p>
          </div>
        </Reveal>
        <div className="grid gap-6 md:grid-cols-3">
          {progress.map(({ icon: Icon, title, body, tag }, i) => (
            <Reveal key={title} delay={i * 0.08}>
              <TiltCard tilt={6}>
                <div className="flex h-full flex-col rounded-2xl border border-white/15 border-l-4 border-l-[#4AAC00] bg-[#020b1a]/90 backdrop-blur-xl p-7 shadow-lg hover:bg-[#020b1a] hover:border-[#4AAC00]/55 hover:shadow-xl transition-all duration-200">
                  <motion.div
                    whileHover={{ rotate: -8, scale: 1.08 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 15 }}
                    className="mb-5 inline-flex w-fit rounded-xl p-3 border"
                    style={{ background: '#4AAC0022', borderColor: '#4AAC0044' }}
                  >
                    <Icon className="h-6 w-6" style={{ color: '#4AAC00' }} />
                  </motion.div>
                  <h3 className="mb-2 text-white">{title}</h3>
                  <p className="flex-1 text-white/85">{body}</p>
                  <p className="mt-5 text-xs font-bold text-[#3b82f6] uppercase tracking-widest">{tag}</p>
                </div>
              </TiltCard>
            </Reveal>
          ))}
        </div>
      </section>

      <StackSection theme="navy" imageSrc={imgDataNetwork}>
        <SectionHeading
          eyebrow="Timing is everything"
          theme="navy"
          title={<ScrambleText text="A category forming in real time." />}
        />
        <Reveal className="mt-10 mb-10 rounded-2xl border border-white/15 bg-[#020b1a]/90 backdrop-blur-xl p-6 md:p-8">
          <AdoptionTimeline />
        </Reveal>
        <div className="grid gap-6 md:grid-cols-2">
          {timing.map(({ icon: Icon, title, body }, i) => (
            <Reveal key={title} delay={i * 0.06}>
              <TiltCard>
                <div className="flex h-full items-start gap-5 rounded-2xl border border-white/15 border-l-4 border-l-[#074EDC] bg-[#020b1a]/90 backdrop-blur-xl p-7 hover:bg-[#020b1a] hover:border-[#074EDC]/55 hover:shadow-xl transition-all duration-200">
                  <motion.div
                    whileHover={{ rotate: -8, scale: 1.08 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 15 }}
                    className="inline-flex flex-shrink-0 rounded-xl bg-[#074EDC] p-3"
                  >
                    <Icon className="h-6 w-6 text-white" />
                  </motion.div>
                  <div>
                    <h3 className="mb-2 text-white">{title}</h3>
                    <p className="text-white/85">{body}</p>
                  </div>
                </div>
              </TiltCard>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-12 rounded-2xl border border-white/15 bg-[#020b1a]/90 backdrop-blur-xl p-6 md:p-8">
          <WorkPipelineFlow />
        </Reveal>
      </StackSection>

      {/* WHERE WE ARE NOW */}
      <StackSection theme="navy" imageSrc={imgDataCity}>
        <SectionHeading
          eyebrow="Momentum"
          theme="navy"
          title="Where we are right now."
          intro="We would rather show honest progress than overstate our stage. Here is where TICCA stands today and where we are heading."
        />
        <Reveal className="mt-10 rounded-2xl border border-white/15 bg-[#020b1a]/90 backdrop-blur-xl p-6 md:p-8">
          <LiveWorkConsole />
        </Reveal>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {progress.map(({ icon: Icon, title, body, tag }, i) => (
            <Reveal key={title} delay={i * 0.08}>
              <TiltCard tilt={6}>
                <div className="flex h-full flex-col rounded-2xl border border-white/15 border-l-4 border-l-[#074EDC] bg-[#020b1a]/90 backdrop-blur-xl p-7 shadow-lg hover:bg-[#020b1a] hover:border-[#074EDC]/55 hover:shadow-xl transition-all duration-200">
                  <motion.div
                    whileHover={{ rotate: -8, scale: 1.08 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 15 }}
                    className="mb-5 inline-flex w-fit rounded-xl bg-[#074EDC] p-3"
                  >
                    <Icon className="h-6 w-6 text-white" />
                  </motion.div>
                  <h3 className="mb-2 text-white">{title}</h3>
                  <p className="flex-1 text-white/85">{body}</p>
                  <p className="mt-5 text-xs font-bold text-[#3b82f6] uppercase tracking-widest">{tag}</p>
                </div>
              </TiltCard>
            </Reveal>
          ))}
        </div>
      </StackSection>

      {/* JOIN US */}
      <StackSection theme="navy" imageSrc={imgMatrixRain}>
        <Reveal className="mx-auto max-w-2xl text-center">
          <span className="mb-5 inline-block uppercase tracking-[0.2em] text-[#3b82f6]">Join Us</span>
          <h2 style={{ color: '#4AAC00' }}>Stay connected as we build.</h2>
          <p className="mx-auto mt-5 max-w-xl text-white/85 leading-relaxed">
            Stay connected with us as we continue our journey. Your support is invaluable as we work
            toward creating systems that make AI-assisted work accountable for teams everywhere.
          </p>
          <motion.button
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleInvestor}
            className="group mt-8 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#074EDC] to-[#0540b8] px-10 py-4 text-white font-semibold shadow-[0_0_40px_-8px_rgba(7,78,220,0.5)]"
          >
            Request investor access
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </motion.button>
        </Reveal>
      </StackSection>
    </div>
  );
}


export default WhenPage;
